// src/pages/Calendar.tsx
/**
 * Calendar 頁面
 *
 * 提供多視圖日曆功能，支援：
 * - 月視圖、週視圖、日視圖、三日視圖
 * - TimeBox 和 Event 的創建、編輯、刪除
 * - 拖拽重新排程（含樂觀更新）
 * - AI 排程規劃
 * - 響應式設計（桌面版/移動版）
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useHeader } from '../hooks/useHeader';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useOnboarding, TipTooltip, useTipAutoMarker } from '../features/onboarding';
import TimeBoxDetail from '../features/timebox/components/TimeBoxDetail';
import EventDetail from '../features/event/components/EventDetail';
import { QuickCreateModal } from '../features/schedule';
import {
  ThreeDayView,
  DayView,
  WeekView,
  MonthView,
  useCalendarItems,
  useCalendarModals,
  usePlacementMode,
  useCalendarHandlers,
  SIDEBAR_WIDTH_EXPANDED,
  SIDEBAR_WIDTH_TABLET,
  SIDEBAR_WIDTH_COMPACT,
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_PADDING,
} from '../features/calendar';
import type { CalendarView } from '../features/calendar';
import { calculateDateRange, testDateRangeBoundaries } from '../utils/calendarHelper';
import { getCurrentUserId } from '../services/auth';
import { useToast } from '../contexts/ToastContext';
import DynamicFAB from '../components/common/DynamicFAB';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorState from '../components/common/ErrorState';
import { Sparkles, CalendarDays } from '../components/common/icons';
import { useScheduleGeneratorTrigger } from '../features/scheduleGenerator';
import { Lesson2Guide } from '../features/onboarding/components/Lesson2Guide';
import { lessonsService } from '../features/onboarding/services/lessonsService';
import { useLessonsProgress } from '../features/onboarding/hooks/useLessonsProgress';
import { eventBus, EVENTS } from '../utils/eventBus';
import PlannedLayer from '../components/calendar/PlannedLayer';
import PlacementActionBar from '../components/calendar/PlannedLayer/PlacementActionBar';
import { Task } from '../types/task';
import { useTasks } from '../hooks/useTasks';
import { useIdealWeek } from '../features/idealWeek/hooks/useIdealWeek';
import { logger } from '../utils/logger';

const Calendar: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightTaskId = searchParams.get('highlightTaskId');
  const dateParam = searchParams.get('date');
  const location = useLocation();

  const { showSuccess, showError } = useToast();
  const { triggerDayDraft } = useScheduleGeneratorTrigger();
  const { getActiveTipForPage, dismissAndShowNext, shouldShowTip } = useOnboarding();
  useTipAutoMarker();

  // Lesson 2 引導：從 LessonsPanel 導航至 Calendar 時帶有 lessonId state
  const { isLessonCompleted } = useLessonsProgress();
  const incomingLessonId = (location.state as { lessonId?: string } | null)?.lessonId;
  const [showLesson2Guide, setShowLesson2Guide] = useState(incomingLessonId === 'lesson-2');
  useEffect(() => {
    if (incomingLessonId === 'lesson-2') setShowLesson2Guide(true);
  }, [incomingLessonId]);

  // 當 lesson-2 尚未完成時，AI Plan 套用後自動標記完成並通知 Guide 切換到 done 步驟
  const handlePlanApplied = useCallback(() => {
    if (!isLessonCompleted('lesson-2')) {
      lessonsService.markCompleted('lesson-2');
      eventBus.emit(EVENTS.LESSON.PLAN_APPLIED);
    }
  }, [isLessonCompleted]);
  const isMobile = useMediaQuery('(max-width: 640px)');

  const isSmallTablet = useMediaQuery('(min-width: 641px) and (max-width: 1024px)');
  const isTablet = useMediaQuery('(min-width: 641px) and (max-width: 1280px)');
  const [view, setView] = useState<CalendarView>(isMobile ? 'threeDays' : 'week');
  const [date, setDate] = useState<Date>(new Date());

  // Header 配置
  const goToToday = useCallback(() => {
    setDate(new Date());
  }, []);

  const HeaderActions = useMemo(() => [
    <button
      key="today"
      onClick={goToToday}
      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
      aria-label="Back to Today"
    >
      <CalendarDays className="h-5 w-5" />
    </button>
  ], [goToToday]);

  const headerConfig = useMemo(() => ({
    title: 'Calendar',
    rightActions: HeaderActions
  }), [HeaderActions]);

  useHeader(headerConfig);

  // 處理從 TaskDetail Schedule 按鈕跳轉的 date 參數
  useEffect(() => {
    if (dateParam) {
      const targetDate = new Date(dateParam + 'T00:00:00');
      if (!isNaN(targetDate.getTime())) {
        setDate(targetDate);
      }
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('date');
      setSearchParams(nextParams, { replace: true });
    }
  }, [dateParam, searchParams, setSearchParams]);

  // 清除高亮 Task 的 URL 參數
  const clearHighlightTaskId = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('highlightTaskId');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // 離線模式（保留供未來使用）
  const [isOfflineMode] = useState<boolean>(false);

  // Planned Layer 狀態
  const [isMobileTrayExpanded, setIsMobileTrayExpanded] = useState<boolean>(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);

  // Tip 邏輯：取得當前頁面應顯示的 Tip
  // showLesson2Guide 為 true 時，強制不顯示一般 Tip
  // （Lesson2Guide 的 useEffect 在 render 後才執行，無法在第一次 render 時抑制 tip）
  const rawActiveTip = showLesson2Guide ? null : getActiveTipForPage('calendar');
  const activeTip = useMemo(() => {
    if (!rawActiveTip) return null;

    const calendarQueue = ['calendar-planned-layer', 'calendar-planned-layer-mobile', 'calendar-click-slot', 'calendar-ai-planning'];
    const startIndex = calendarQueue.indexOf(rawActiveTip);
    if (startIndex === -1) return rawActiveTip;

    for (let i = startIndex; i < calendarQueue.length; i++) {
      const tipId = calendarQueue[i];
      if (!shouldShowTip(tipId)) continue;

      if (tipId === 'calendar-planned-layer') {
        if (!isMobile && !isSidebarCollapsed) return tipId;
      } else if (tipId === 'calendar-planned-layer-mobile') {
        if (isMobile) return tipId;
      } else {
        return tipId;
      }
    }

    return null;
  }, [rawActiveTip, isMobile, isSidebarCollapsed, shouldShowTip]);

  // 放置模式（行動版）
  const {
    placementMode,
    handleEnterPlacementMode,
    handleExitPlacementMode,
    handleSelectTimeSlot,
    handleConfirmPlacement,
    handleCancelPreview,
  } = usePlacementMode({
    onRefresh: () => refreshData(),
    onError: showError,
    setIsMobileTrayExpanded,
  });

  // 動態計算右側欄寬度
  const getSidebarWidth = useCallback(() => {
    if (isSidebarCollapsed) return SIDEBAR_WIDTH_COLLAPSED;
    if (isMobile) return 0;
    if (isSmallTablet) return SIDEBAR_WIDTH_COMPACT;
    if (isTablet) return SIDEBAR_WIDTH_TABLET;
    return SIDEBAR_WIDTH_EXPANDED;
  }, [isSidebarCollapsed, isMobile, isSmallTablet, isTablet]);

  const currentSidebarWidth = getSidebarWidth();

  // 觸控拖曳處理器 ref（由 WeekView 設置）
  const taskTouchStartHandlerRef = useRef<((task: Task, touch: Touch) => void) | null>(null);

  // 獲取任務數據
  const userId = getCurrentUserId();
  const { tasks: allTasks } = useTasks(userId);
  const { idealWeek } = useIdealWeek(userId);

  const getTaskById = useCallback((taskId: string): Task | undefined => {
    return allTasks.find(task => task.id === taskId);
  }, [allTasks]);

  // 計算日期範圍 for useCalendarItems
  const dateRange = useMemo(() => {
    if (view === 'day') return undefined;

    const result = calculateDateRange(date, view);
    logger.dev(`Calendar - ${view} view date range:`, {
      view,
      startDate: result.startDate.toDateString(),
      endDate: result.endDate.toDateString()
    });
    return result;
  }, [date.getTime(), view]); // eslint-disable-line react-hooks/exhaustive-deps

  // 使用 useCalendarItems hook
  const {
    scheduleItems,
    isLoading,
    error,
    refresh: refreshData
  } = useCalendarItems(date, {
    dateRange,
    useRealtime: true,
    autoRefresh: true
  });

  // Modal 管理
  const modals = useCalendarModals({
    onRefresh: refreshData,
    onSuccess: showSuccess,
    onError: showError,
  });

  // 日曆事件處理器
  const {
    displayScheduleItems,
    handleSelectScheduleItem,
    handleSelectSlot,
    handleReschedule,
    handleCreateTimeBoxFromTask,
    handleAddTaskToTimeBox,
    handleQuickScheduleTask,
    handlePrev,
    handleNext,
    handleToday,
    handleDateSelect,
  } = useCalendarHandlers({
    view,
    date,
    setDate,
    scheduleItems,
    modals,
    refreshData,
    showSuccess,
    showError,
  });

  // 根據設備自動切換視圖
  useEffect(() => {
    setView(isMobile ? 'threeDays' : 'week');
  }, [isMobile]);

  // 開發環境下的邊界測試
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      testDateRangeBoundaries();
    }
  }, []);

  // Planned Layer 事件處理
  const handleToggleMobileTray = useCallback(() => {
    setIsMobileTrayExpanded(prev => !prev);
  }, []);

  const handleTaskDragStart = useCallback((_task: Task, _e: React.DragEvent) => {
    // 實際拖曳邏輯由 useCalendarDragDrop hook 處理
  }, []);

  const handleTaskDragEnd = useCallback(() => {
    // 實際拖曳邏輯由 useCalendarDragDrop hook 處理
  }, []);

  const handleTaskTouchStart = useCallback((task: Task, e: React.TouchEvent) => {
    if (!taskTouchStartHandlerRef.current) return;
    if (e.touches.length !== 1) return;
    const touch = e.touches[0] as unknown as Touch;
    taskTouchStartHandlerRef.current(task, touch);
  }, []);

  // 渲染當前選定的視圖
  const renderCalendarView = () => {
    if (isLoading) {
      return <LoadingSpinner size="lg" className="h-full" />;
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-full p-4">
          <ErrorState
            message={error?.message || 'An error occurred'}
            onRetry={refreshData}
          />
        </div>
      );
    }

    if (view === 'threeDays') {
      return (
        <ThreeDayView
          date={date}
          scheduleItems={displayScheduleItems}
          onSelectTimeBox={handleSelectScheduleItem}
          onSelectSlot={handleSelectSlot}
          onNavigatePrev={handlePrev}
          onNavigateNext={handleNext}
          onNavigateToday={handleToday}
          onDateSelect={handleDateSelect}
          onReschedule={handleReschedule}
          placementMode={isMobile ? placementMode : undefined}
          onSelectTimeSlotForPlacement={isMobile ? handleSelectTimeSlot : undefined}
          onConfirmPlacement={isMobile ? handleConfirmPlacement : undefined}
          idealWeek={idealWeek}
        />
      );
    }

    if (view === 'day') {
      return (
        <DayView
          date={date}
          scheduleItems={displayScheduleItems}
          onSelectTimeBox={handleSelectScheduleItem}
          onSelectSlot={handleSelectSlot}
          onNavigatePrev={handlePrev}
          onNavigateNext={handleNext}
          onNavigateToday={handleToday}
          onDateSelect={handleDateSelect}
          onReschedule={handleReschedule}
          onCreateTimeBoxFromTask={handleCreateTimeBoxFromTask}
          onAddTaskToTimeBox={handleAddTaskToTimeBox}
          getTaskById={getTaskById}
          onTouchDragReady={(handler) => {
            taskTouchStartHandlerRef.current = handler;
          }}
          idealWeek={idealWeek}
        />
      );
    }

    if (view === 'week') {
      return (
        <WeekView
          date={date}
          scheduleItems={displayScheduleItems}
          onSelectTimeBox={handleSelectScheduleItem}
          onSelectSlot={handleSelectSlot}
          onNavigatePrev={handlePrev}
          onNavigateNext={handleNext}
          onNavigateToday={handleToday}
          onDateSelect={handleDateSelect}
          onReschedule={handleReschedule}
          onCreateTimeBoxFromTask={handleCreateTimeBoxFromTask}
          onAddTaskToTimeBox={handleAddTaskToTimeBox}
          getTaskById={getTaskById}
          onTouchDragReady={(handler) => {
            taskTouchStartHandlerRef.current = handler;
          }}
          placementMode={isMobile ? placementMode : undefined}
          onSelectTimeSlotForPlacement={isMobile ? handleSelectTimeSlot : undefined}
          onConfirmPlacement={isMobile ? handleConfirmPlacement : undefined}
          idealWeek={idealWeek}
        />
      );
    }

    if (view === 'month') {
      return (
        <MonthView
          date={date}
          scheduleItems={displayScheduleItems}
          onSelectTimeBox={handleSelectScheduleItem}
          onSelectSlot={handleSelectSlot}
          onNavigatePrev={handlePrev}
          onNavigateNext={handleNext}
          onNavigateToday={handleToday}
          onDateSelect={handleDateSelect}
          idealWeek={idealWeek}
        />
      );
    }

    return null;
  };

  return (
    <div className="h-full flex flex-row overflow-hidden px-5 md:px-6 pb-4 md:pb-6">
      {/* 左側主要內容區 */}
      <div className="flex-1 flex flex-col min-w-0 h-full">
        {/* 離線模式提示 */}
        {isOfflineMode && (
          <div className="bg-yellow-50 text-yellow-800 text-center py-2 text-sm border-b border-yellow-200 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>Offline mode, display cache data</span>
            <button
              onClick={refreshData}
              className="ml-3 px-2 py-1 bg-yellow-100 rounded-md text-xs hover:bg-yellow-200 transition-colors"
            >
              reconnect
            </button>
          </div>
        )}

        {/* 日曆控制區 - 桌面版 */}
        {!isMobile && (
          <div className="flex flex-wrap items-center justify-between py-3 border-b border-[var(--border-default)]">
            <div className="flex items-center pl-4">
              <div className="flex rounded-md bg-[var(--border-default)]/40 p-1">
                <button
                  onClick={() => setView('month')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'month'
                    ? 'bg-surface-paper text-sky-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                    }`}
                >
                  Month
                </button>
                <button
                  onClick={() => setView('week')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'week'
                    ? 'bg-surface-paper text-sky-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                    }`}
                >
                  Week
                </button>
                <button
                  onClick={() => setView('day')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'day'
                    ? 'bg-surface-paper text-sky-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                    }`}
                >
                  Day
                </button>
              </div>
            </div>

            <div className="relative mr-4 z-toolbar">
              <button
                data-lesson-anchor="ai-plan-button"
                onClick={() => {
                  triggerDayDraft({ onApplied: handlePlanApplied });
                }}
                className="flex items-center gap-2 px-4 py-2 bg-sky-100 text-sky-700 rounded-md hover:bg-sky-200 transition-colors"
              >
                <Sparkles size={16} />
                <span>Draft</span>
              </button>
              {activeTip === 'calendar-ai-planning' && (
                <TipTooltip
                  tipId="calendar-ai-planning"
                  onClose={() => dismissAndShowNext('calendar-ai-planning', 'calendar')}
                />
              )}
            </div>
          </div>
        )}

        {/* 主要內容區 */}
        <div className="flex-1 overflow-hidden min-h-0 relative">
          <div className="flex h-full">
            <div className="flex-1 min-w-0 relative">
              {renderCalendarView()}
            </div>
          </div>
        </div>
      </div>

      {/* Tip: 點擊空時段 */}
      {activeTip === 'calendar-click-slot' && (
        <div className="fixed top-[55%] left-1/2 -translate-x-1/2 pointer-events-auto">
          <TipTooltip
            tipId="calendar-click-slot"
            position="bottom"
            arrowAlign="center"
            onClose={() => dismissAndShowNext('calendar-click-slot', 'calendar')}
          />
        </div>
      )}

      {/* 右側 Sidebar */}
      {!isMobile && (view === 'day' || view === 'week') && (
        <div
          className="flex-shrink-0 h-full py-4 pl-2 pr-2 z-floating transition-all duration-300 relative"
          style={{ width: currentSidebarWidth + SIDEBAR_PADDING }}
        >
          <PlannedLayer
            isMobile={false}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            onDragStart={handleTaskDragStart}
            onDragEnd={handleTaskDragEnd}
            onTouchStart={handleTaskTouchStart}
            highlightTaskId={highlightTaskId}
            onClearHighlight={clearHighlightTaskId}
          />
          {activeTip === 'calendar-planned-layer' && !isSidebarCollapsed && (
            <div className="absolute top-40 right-full">
              <TipTooltip
                tipId="calendar-planned-layer"
                onClose={() => dismissAndShowNext('calendar-planned-layer', 'calendar')}
              />
            </div>
          )}
        </div>
      )}

      {/* 行動版 Planned Layer 托盤 */}
      {isMobile && view === 'threeDays' && placementMode.state === 'idle' && (
        <>
          <PlannedLayer
            isMobile={true}
            isExpanded={isMobileTrayExpanded}
            onToggle={handleToggleMobileTray}
            onQuickSchedule={handleQuickScheduleTask}
            onEnterPlacementMode={handleEnterPlacementMode}
            highlightTaskId={highlightTaskId}
            onClearHighlight={clearHighlightTaskId}
          />
          {activeTip === 'calendar-planned-layer-mobile' && (
            <div
              className="fixed left-4"
              style={{
                bottom: isMobileTrayExpanded ? 'calc(30vh + 4rem + 0.5rem)' : 'calc(3.5rem + 4rem + 0.5rem)'
              }}
            >
              <TipTooltip
                tipId="calendar-planned-layer-mobile"
                onClose={() => dismissAndShowNext('calendar-planned-layer-mobile', 'calendar')}
              />
            </div>
          )}
        </>
      )}

      {/* 行動版 FAB 按鈕 */}
      {isMobile && placementMode.state === 'idle' && (
        <>
          <div
            className="fixed right-6 z-floating transition-all duration-300"
            style={{
              bottom: isMobileTrayExpanded ? 'calc(30vh + 24px)' : '88px'
            }}
          >
            <div className="relative">
              <DynamicFAB
                onClick={() => {
                  triggerDayDraft({ onApplied: handlePlanApplied });
                }}
                icon={<Sparkles size={24} />}
                color="purple"
                ariaLabel="Draft"
              />
              {activeTip === 'calendar-ai-planning' && (
                <TipTooltip
                  tipId="calendar-ai-planning"
                  onClose={() => dismissAndShowNext('calendar-ai-planning', 'calendar')}
                />
              )}
            </div>
          </div>
        </>
      )}

      {/* 行動版放置模式操作列 */}
      {isMobile && placementMode.state !== 'idle' && placementMode.selectedTask && (
        <PlacementActionBar
          selectedTask={placementMode.selectedTask}
          previewSlot={placementMode.previewSlot}
          onCancel={placementMode.state === 'previewing' ? handleCancelPreview : handleExitPlacementMode}
          onConfirm={placementMode.state === 'previewing' ? handleConfirmPlacement : undefined}
        />
      )}

      {/* TimeBox 詳情 */}
      <TimeBoxDetail
        isOpen={modals.timeBoxDetail.isOpen}
        timeBoxId={modals.timeBoxDetail.selectedId}
        onClose={modals.timeBoxDetail.close}
        onRefresh={refreshData}
        mode="edit"
      />

      {/* Event 詳情 */}
      <EventDetail
        isOpen={modals.eventDetail.isOpen}
        eventId={modals.eventDetail.selectedId}
        onClose={modals.eventDetail.close}
        onRefresh={refreshData}
      />

      {/* QuickCreate Modal */}
      <QuickCreateModal
        isOpen={modals.quickCreateModal.isOpen}
        onClose={modals.quickCreateModal.close}
        onSuccess={modals.quickCreateModal.onSuccess}
        startTime={modals.quickCreateModal.startTime}
        endTime={modals.quickCreateModal.endTime}
      />

      {/* Onboarding Lesson 2 引導（Portal，fixed 右下角浮動卡片） */}
      {showLesson2Guide && (
        <Lesson2Guide
          onClose={() => setShowLesson2Guide(false)}
        />
      )}
    </div>
  );
};

export default Calendar;
